import { useCallback, useEffect, useState } from 'react';
import { askJeffApi } from '../../api/askJeffApi';
import { useAuth } from '../../hooks/useAuth';
import { AskJeffNoteDialog } from './AskJeffNoteDialog';

interface AskJeffButtonProps {
  /** The hand's cardsgamehands tableresults.id. */
  tableResultId: number | undefined;
  /** Where the question was asked: a module key, or 'overcalling' / 'counting'. */
  sectionKey: string;
  /**
   * Layout classes added to the button's own look (its width or flex share in the row it joins).
   * The colours and shape stay the component's, so it reads the same on every page.
   */
  className?: string;
}

/**
 * The "Ask Jeff" control (feature 025) — flags the hand on screen for Jeff's commentary and
 * carries the Super Editor's note about his doubt.
 *
 * <p>
 * Renders for the Super Editor and NOBODY else, Admin included. The API enforces the same rule,
 * so this check is about not showing a control that would only 403; it is not the security
 * boundary. Every other role gets nothing at all — not a disabled button.
 * </p>
 *
 * <p>
 * Once flagged the button says so and re-opens the same dialog to view or edit the note, which
 * is why there is one component rather than a button plus a separate "edit notes" affordance.
 * </p>
 */
export function AskJeffButton({ tableResultId, sectionKey, className }: AskJeffButtonProps) {
  const { user } = useAuth();
  const isSuperEditor = user?.role === 'Super Editor';

  const [isFlagged, setIsFlagged] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reload the flag state whenever the hand changes — the practice pages keep this component
  // mounted and swap the id underneath it as the learner advances.
  useEffect(() => {
    if (!isSuperEditor || !tableResultId) {
      setIsFlagged(false);
      setNote(null);
      return;
    }

    let cancelled = false;
    askJeffApi
      .getFlags([tableResultId])
      .then(flags => {
        if (cancelled) return;
        const flag = flags.find(f => f.tableResultId === tableResultId);
        setIsFlagged(!!flag);
        setNote(flag?.note ?? null);
      })
      // A failed status read must not break the practice page: fall back to "not flagged",
      // which at worst offers to flag a hand that already is (the upsert handles that).
      .catch(() => {
        if (!cancelled) {
          setIsFlagged(false);
          setNote(null);
        }
      });

    return () => { cancelled = true; };
  }, [isSuperEditor, tableResultId]);

  const handleSave = useCallback(async (nextNote: string | null) => {
    if (!tableResultId) return;

    setIsSaving(true);
    setError(null);
    try {
      if (isFlagged) {
        await askJeffApi.updateNote(tableResultId, nextNote);
      } else {
        await askJeffApi.flagHand(tableResultId, sectionKey, nextNote);
      }
      setIsFlagged(true);
      setNote(nextNote);
      setDialogOpen(false);
    } catch {
      setError('Could not save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [tableResultId, sectionKey, isFlagged]);

  if (!isSuperEditor || !tableResultId) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => { setError(null); setDialogOpen(true); }}
        data-testid="ask-jeff-button"
        aria-label={isFlagged ? 'Flagged for Jeff — view or edit your notes' : 'Ask Jeff about this hand'}
        // Shaped like the buttons it sits beside in the answer panel (Skip Hand, Next hand), not a
        // pill of its own: it rides in their row rather than in a footer row under the table, which
        // took height the phones did not have and clipped Skip Hand, Next and Counting's playback
        // strip (feature 033, T051). The caller says how much of the row it takes.
        className={
          'min-h-[24px] rounded-lg px-3 py-2 text-[15px] font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-brass ' +
          (isFlagged
            ? 'border border-brass bg-cream text-brass-text hover:bg-paper'
            : 'bg-brass text-on-brass hover:bg-brass-hover') +
          (className ? ` ${className}` : '')
        }
      >
        {isFlagged ? '✓ Flagged for Jeff' : 'Ask Jeff'}
      </button>

      <AskJeffNoteDialog
        isOpen={dialogOpen}
        note={note}
        isFlagged={isFlagged}
        isSaving={isSaving}
        error={error}
        onSave={handleSave}
        onCancel={() => setDialogOpen(false)}
      />
    </>
  );
}
